import React, { useState, useMemo } from 'react'
import { useParams, useNavigate, useLoaderData } from 'react-router-dom'
import { t } from '@lingui/core/macro'
import { payApple } from '../../api/payApple'
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation'
import { loadExtraFieldsFromStorage, saveFieldValueToStorage } from '../../utils/extraFieldsStorage'
import { extraFieldShowFilter } from '../../utils/filters'
import { formatNumber } from '../../utils/formatters'
import { paymentErrorHandler } from '../../utils/paymentErrorHandler'
import { PaymentPageInfo } from '../../types/paymentPageInfo'
import Money from '../../types/Money'
import Details from '../../components/Details/Details'
import PaymentError from '../../components/PaymentError/PaymentError'
import Expired from '../../components/Expired/Expired'
import TimeLeft from '../../components/TimeLeft/TimeLeft'
import InputForm from '../../components/InputForm/InputForm'
import MoneyInputForm from '../../components/InputForm/MoneyInputForm'
import PaymentMethodList from '../../components/PaymentMethodList/PaymentMethodList'
import ApplePaymentMethod from '../../components/PaymentMethods/ApplePaymentMethod'
import CardPaymentMethod from '../../components/PaymentMethods/CardPaymentMethod'
import P2PPaymentMethod from '../../components/PaymentMethods/P2PPaymentMethod'
import PixPaymentMethod from '../../components/PaymentMethods/PixPaymentMethod'
import styles from './PaymentPage.module.css'

const PaymentPage = () => {
    const navigate = useNavigate()

    const { method } = useParams<{ method?: string }>()

    const paymentPageInfo = useLoaderData<PaymentPageInfo>()

    const [isExpired, setIsExpired] = useState(paymentPageInfo.expiresAt && new Date(paymentPageInfo.expiresAt) < new Date())
    const [paymentError, setPaymentError] = useState(false)

    const storedData = loadExtraFieldsFromStorage(paymentPageInfo.id)

    const [email, setEmail] = useState(storedData.email ?? (paymentPageInfo.email.value || ''))
    const [editableMoney, setEditableMoney] = useState<Money>(
        {
            amount: storedData.amount ?? (paymentPageInfo.money.amount ? (paymentPageInfo.money.amount / 100).toFixed() : ''),
            currency: storedData.currency ?? (paymentPageInfo.money.currency || 'USD'),
        }
    )

    const emailEditable = !paymentPageInfo.email.value || paymentPageInfo.email.editable
    const moneyEditable = !paymentPageInfo.money.amount || paymentPageInfo.money.editable

    const validationRules = useMemo(() => {
        const rules: ValidationRules = {}

        if (emailEditable) {
            rules.email = {
                required: true,
                validation: (email: string): string | null => {
                    const cleanEmail = email.trim()

                    if (cleanEmail.length === 0) {
                        return t`Email не должен быть пустым`
                    }

                    if (cleanEmail.length < 3 || cleanEmail.length > 100) {
                        return t`Email должен содержать от 6 до 100 символов`
                    }

                    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(cleanEmail)) {
                        return t`Некорректный Email`
                    }

                    return null
                },
            }
        }

        if (moneyEditable) {
            rules.amount = {
                required: true,
                validation: (amount: string): string | null => {
                    const cleanAmount = amount.trim()

                    if (cleanAmount.length === 0) {
                        return t`Сумма не должна быть пустой`
                    }

                    if (!/^[1-9]\d*(\.\d{1,2})?$|^0\.\d{1,2}$/.test(cleanAmount)) {
                        return t`Некорректная сумма`
                    }

                    return null
                },
            }
        }

        return rules
    }, [emailEditable, moneyEditable])

    const { errors, setError, removeError, validateAllFields } = useFormValidation(validationRules)

    const findPaymentMethodType = (methodId: string | undefined) => paymentPageInfo.paymentMethodTypes.find(pm => pm.type === methodId)

    const handleChangeCurrency = (currency: string) => {
        saveFieldValueToStorage(paymentPageInfo.id, 'currency', currency)
        setEditableMoney({...editableMoney, currency: currency})
    }

    const handleChangeAmount = (amount: string) => {
        const cleanAmount = amount.replace(/(\.\d{2})\d+/g, '$1')

        saveFieldValueToStorage(paymentPageInfo.id, 'amount', cleanAmount)
        setEditableMoney({...editableMoney, amount: cleanAmount})
        removeError('amount')
    }

    const handleChangeEmail = (email: string) => {
        saveFieldValueToStorage(paymentPageInfo.id, 'email', email)
        setEmail(email)
        removeError('email')
    }

    const handlePaymentMethodClick = async (methodId: string) => {
        if (!validateAllFields({ email: email, ...(moneyEditable ? { amount: editableMoney.amount} : {})})) {
            return
        }

        // Если нет дополнительных полей - сразу обрабатываем платеж (кроме карт, там еще логика перехода на acs)
        const noExtraFields = !findPaymentMethodType(methodId)?.extraFields.filter(extraFieldShowFilter).length
        const noPaymentFieldsMethods = ['apple']

        if (noExtraFields && noPaymentFieldsMethods.includes(methodId)) {
            try {
                if (methodId === 'apple') {
                    const result = await payApple(paymentPageInfo.id, { email: email }, moneyEditable ? editableMoney : undefined)

                    navigate(`/payment/${paymentPageInfo.id}/apple/${result.transactionId}`)
                }
            } catch (error) {
                paymentErrorHandler(error, () => {}, setError, setPaymentError)
            }

            return
        }

        navigate(`/payment/${paymentPageInfo.id}/${methodId}`)
    }

    const renderPaymentMethod = () => {
        const paymentMethodData = findPaymentMethodType(method)

        if (!paymentMethodData) {
            return null
        }

        if (method === 'card') {
            return <CardPaymentMethod
                paymentPageInfo={paymentPageInfo}
                paymentMethod={paymentMethodData}
                money={moneyEditable ? editableMoney : undefined}
                email={email}
                setParentError={setError}
                validateParentFields={validateAllFields}
                setPaymentError={setPaymentError}
            />
        }

        if (method === 'p2p') {
            return <P2PPaymentMethod
                paymentPageInfo={paymentPageInfo}
                paymentMethod={paymentMethodData}
                money={moneyEditable ? editableMoney : undefined}
                email={email}
                setParentError={setError}
                validateParentFields={validateAllFields}
                setPaymentError={setPaymentError}
            />
        }

        if (method === 'pix') {
            return <PixPaymentMethod
                paymentPageInfo={paymentPageInfo}
                paymentMethod={paymentMethodData}
                money={moneyEditable ? editableMoney : undefined}
                email={email}
                setParentError={setError}
                validateParentFields={validateAllFields}
                setPaymentError={setPaymentError}
            />
        }

        if (method === 'apple') {
            return <ApplePaymentMethod
                paymentPageInfo={paymentPageInfo}
                paymentMethod={paymentMethodData}
                money={moneyEditable ? editableMoney : undefined}
                email={email}
                setParentError={setError}
                validateParentFields={validateAllFields}
                setPaymentError={setPaymentError}
            />
        }

        return (
            <div className={styles.methodPlaceholder}>
                <h3>{t`Метод оплаты:`} {method}</h3>
                <p>{t`Оплата этим методом временно недоступна`}</p>
                <button onClick={() => navigate(`/payment/${paymentPageInfo.id}`)} className={styles.backButton}>
                    {t`← Вернуться к выбору метода`}
                </button>
            </div>
        )
    }

    if (isExpired) {
        return (
            <Expired
                orderId={paymentPageInfo.orderId}
                redirectUrl={paymentPageInfo.redirectUrl}
                redirectTimeout={paymentPageInfo.redirectTimeout}
            />
        )
    }

    if (paymentError) {
        return (
            <PaymentError
                paymentPageInfo={paymentPageInfo}
                removePaymentError={() => setPaymentError(false)}
            />
        )
    }

    const details = [
        ...(paymentPageInfo.orderId ? [{ label: t`Номер заказа:`, value: paymentPageInfo.orderId }] : []),
        ...(paymentPageInfo.description ? [{ label: t`Описание:`, value: paymentPageInfo.description }] : []),
    ]

    return (
        <>
            <TimeLeft
                expiresAt={paymentPageInfo.expiresAt}
                onExpired={() => setIsExpired(true)}
            />

            {!!details.length && <Details items={details} accentValues={false} />}

            <div className={styles.amountSection}>
                {moneyEditable ? (
                    <MoneyInputForm
                        money={editableMoney}
                        onChangeAmount={handleChangeAmount}
                        onChangeCurrency={handleChangeCurrency}
                        errorMessage={errors.amount || undefined}
                    />
                ) : (
                    <div className={styles.amountDisplay}>
                        {formatNumber(paymentPageInfo.money.amount / 100)}
                        <span className={styles.currency}>{paymentPageInfo.money.currency}</span>
                    </div>
                )}
            </div>

            {emailEditable && (
                <InputForm
                    label="Email *"
                    type="email"
                    name="email"
                    value={email}
                    placeholder="your@email.com"
                    onChange={(e) => handleChangeEmail(e.target.value)}
                    maxLength={50}
                    errorMessage={errors.email || undefined}
                />
            )}

            {method ? renderPaymentMethod() : (
                <PaymentMethodList
                    paymentMethodTypes={paymentPageInfo.paymentMethodTypes}
                    handlePaymentMethodClick={handlePaymentMethodClick}
                />
            )}
        </>
    )
}

export default PaymentPage
